import { Box, Link, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import PasswordInput from "../../components/ui/PasswordInput";
import Text from "../../components/ui/Text";
import { useAuth } from "../../contexts/AuthContext";
import { validateAuthForm } from "../../utils/authValidation";

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;

  const { signup, signInWithGoogle, error, setError } = useAuth();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    general: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Clear global error when component mounts
  useEffect(() => {
    setError(null);
  }, [setError]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: "",
      });
    }
  };

  const validateForm = () => {
    // Use centralized validation
    const newErrors = validateAuthForm(formData, "signup");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle Google Sign Up
  const handleGoogleSignUp = async () => {
    try {
      // Add from to session storage
      if (from) {
        sessionStorage.setItem("postSignupRedirect", from);
      }

      setIsSubmitting(true);
      setError(null);
      await signInWithGoogle();

      navigate("/account-setup", {
        replace: true,
      });
    } catch (error) {
      setErrors((prev) => ({ ...prev, general: error.message }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // Add from to session storage
      if (from) {
        sessionStorage.setItem("postSignupRedirect", from);
      }

      // First, create the user account with Firebase
      await signup(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName,
      );

      // Redirect to confirm email page after successful signup
      navigate("/confirm-email", {
        replace: true,
        state: {
          email: formData.email,
          password: formData.password,
        },
      });
    } catch (error) {
      // Handle specific Firebase errors with inline messages
      if (error.code === "auth/email-already-in-use") {
        setErrors((prev) => ({
          ...prev,
          email: "Email already in use. Try signing in instead.",
          general: "",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          general: error.friendlyMessage || error.message,
        }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Welcome Section */}
      <Box sx={{ mb: 3 }}>
        <Text variant="pageHeader" sx={{ mb: "8px !important" }}>
          Welcome to Findaside
        </Text>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Text
            variant="paragraph3"
            sx={{
              fontSize: "16px",
            }}
          >
            Already a member?
          </Text>
          <Button
            variant="tertiary"
            onClick={() => navigate("/signin")}
            sx={{
              color: "#1890FF",
              fontSize: "14px",
              minWidth: "auto",
              p: 1,
            }}
          >
            Sign in
          </Button>
        </Box>
      </Box>

      <form onSubmit={handleSubmit} noValidate>
        <Stack spacing={3}>
          {/* Google Button */}
          <Button
            variant="secondary"
            onClick={handleGoogleSignUp}
            disabled={isSubmitting}
            startIcon={
              <img
                src="https://developers.google.com/identity/images/g-logo.png"
                alt="Google"
                style={{ width: 18, height: 18 }}
              />
            }
            sx={{
              justifyContent: "center",
              borderRadius: "4px",
              border: "1px solid black",
            }}
          >
            {isSubmitting ? "Signing up..." : "Continue with Google"}
          </Button>

          {/* OR Divider */}
          <Box sx={{ textAlign: "center", position: "relative", my: 2 }}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: 0,
                right: 0,
                height: "1px",
                backgroundColor: "#D4D4D4",
                zIndex: 1,
              }}
            />
            <Text
              variant="paragraph3"
              sx={{
                backgroundColor: "white",
                px: 2,
                position: "relative",
                zIndex: 2,
                color: "#666666",
              }}
            >
              OR
            </Text>
          </Box>

          {/* General Error Message */}
          {(errors.general || error) && (
            <Box
              sx={{
                backgroundColor: "#FFF2F0",
                border: "1px solid #FFCCC7",
                borderRadius: "4px",
                padding: "12px",
              }}
            >
              <Text
                variant="paragraph3"
                sx={{
                  color: "#D32F2F",
                }}
              >
                {errors.general || error}
              </Text>
            </Box>
          )}

          {/* Name Fields */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Input
              label="First name"
              placeholder="First name"
              value={formData.firstName}
              onChange={handleInputChange("firstName")}
              error={!!errors.firstName}
              helperText={errors.firstName}
            />
            <Input
              label="Last name"
              placeholder="Last name"
              value={formData.lastName}
              onChange={handleInputChange("lastName")}
              error={!!errors.lastName}
              helperText={errors.lastName}
            />
          </Box>

          {/* Email */}
          <Input
            label="Email"
            placeholder="Email"
            type="email"
            value={formData.email}
            onChange={handleInputChange("email")}
            error={!!errors.email}
            helperText={errors.email}
          />

          {/* Password */}
          <PasswordInput
            label="Create a password"
            value={formData.password}
            onChange={handleInputChange("password")}
            error={!!errors.password}
            helperText={errors.password}
          />

          {/* Password Requirements */}
          <Text variant="paragraph3" sx={{ color: "#666666" }}>
            At least 8 characters, one upper and lower case letter, one number
            and one symbol.
          </Text>

          {/* Terms and Conditions */}
          <Text variant="paragraph3">
            By clicking Continue, I agree to the Findaside{" "}
            <Link
              href="https://slow-centipede-6ab.notion.site/Terms-and-Conditions-2843273d4063801d9ae2f13950d02c34"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "#1890FF",
                cursor: "pointer",
                textDecoration: "none",
              }}
            >
              Terms of Use
            </Link>{" "}
            and{" "}
            <Link
              href="https://slow-centipede-6ab.notion.site/Privacy-Policy-2843273d40638050b960fcf0dbd1f122"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "#1890FF",
                cursor: "pointer",
                textDecoration: "none",
              }}
            >
              Privacy Policy
            </Link>
          </Text>

          {/* Continue Button */}
          <Button
            variant="primary"
            type="submit"
            disabled={isSubmitting}
            sx={{ mt: 3 }}
          >
            {isSubmitting ? "Creating account..." : "Continue"}
          </Button>
        </Stack>
      </form>
    </>
  );
};

export default Signup;
